/**
 * Centralized SEO configuration.
 *
 * This is the single source of truth for per-route head metadata.
 * The real site origin comes from the VITE_SITE_URL environment variable.
 * No domain is invented here: if the variable is missing we fall back to the
 * browser origin at runtime.
 */

const RAW_SITE_URL = (import.meta.env.VITE_SITE_URL ?? "").trim();

export const SITE_URL: string = RAW_SITE_URL
  ? RAW_SITE_URL.replace(/\/+$/, "")
  : typeof window !== "undefined"
    ? window.location.origin
    : "";

/** Existing absolute image URL already used by the site (favicon / social image). */
export const SEO_IMAGE =
  "https://static.readdy.ai/image/206e3a23f84eeca55b0a1d27380ad96c/abe6e1cb0f267f186911c105a0cc4dcd.jpeg";

export const BRAND_NAME = "301+ Agency";
export const BRAND_SITE_NAME = "301+ Talent & Brand Studio";
export const SOCIAL_LINKS = [
  "https://www.instagram.com/301mgmt",
  "https://www.linkedin.com/company/301mgmt",
];

export interface RouteSeo {
  path: string;
  indexable: boolean;
  lang: string;
  title: string;
  description: string;
  keywords: string;
  robots: string;
  canonicalPath: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogImageAlt: string;
  ogType: string;
  ogSiteName: string;
  ogLocale: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  twitterImageAlt: string;
}

const HOME_SEO: RouteSeo = {
  path: "/",
  indexable: true,
  lang: "es",
  title:
    "301+ Agency | Agencia de Influencer Marketing y Representación de Creadores LATAM",
  description:
    "301+ es la agencia premium de influencer marketing en LATAM. Conectamos marcas con creadores de contenido auténticos para campañas que generan resultados reales, engagement genuino y ROI medible.",
  keywords:
    "agencia influencer marketing, representación creadores contenido, campañas de marca LATAM, marketing digital México",
  robots: "index, follow",
  canonicalPath: "/",
  ogTitle: "301+ MGMT Agency",
  ogDescription:
    "Conectamos marcas con creadores que mueven audiencias reales. Agencia premium de influencer marketing en Latinoamérica.",
  ogImage: SEO_IMAGE,
  ogImageAlt: "301+ MGMT Agency",
  ogType: "website",
  ogSiteName: BRAND_SITE_NAME,
  ogLocale: "es_ES",
  twitterCard: "summary_large_image",
  twitterTitle: "301+ MGMT Agency",
  twitterDescription:
    "Conectamos marcas con creadores que mueven audiencias reales. Agencia premium de influencer marketing en Latinoamérica.",
  twitterImage: SEO_IMAGE,
  twitterImageAlt: "301+ MGMT Agency",
};

function buildNotFoundSeo(pathname: string): RouteSeo {
  const title = "Página no encontrada | 301+ Agency";
  const description =
    "La página que buscas no existe o fue movida. Vuelve al inicio de 301+ para descubrir nuestra agencia de influencer marketing.";
  return {
    path: pathname,
    indexable: false,
    lang: "es",
    title,
    description,
    keywords: "301+ agency",
    robots: "noindex, nofollow",
    canonicalPath: pathname,
    ogTitle: title,
    ogDescription: description,
    ogImage: SEO_IMAGE,
    ogImageAlt: BRAND_NAME,
    ogType: "website",
    ogSiteName: BRAND_SITE_NAME,
    ogLocale: "es_ES",
    twitterCard: "summary_large_image",
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: SEO_IMAGE,
    twitterImageAlt: BRAND_NAME,
  };
}

function normalizePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

export function getRouteSeo(pathname: string): RouteSeo {
  const normalized = normalizePath(pathname);
  if (normalized === "/") {
    return HOME_SEO;
  }
  return buildNotFoundSeo(normalized);
}

export function absoluteUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}